import { useMutation } from "@tanstack/react-query";
import { useAuthenticatedRequestCreator } from "hooks/useRequests";
import { StaffPackage } from "models/packages";
import { ListingType } from "models/pendingListings";
import { DefaultTimeZone } from "models/websiteSettings";
import moment from "moment";
import { CardImageUploadResult } from "./UploadCardImages";

export interface ListingForm {
    cardTitle: string;
    isLot: boolean;
    category: string;
    subCategory: string;
    statusId: string;
    conditionId: string | null;
    grader: string | null;
    grade: string | null;
    package: StaffPackage | undefined;
    frontImage: CardImageUploadResult | undefined;
    backImage: CardImageUploadResult | undefined;
    extraImages: CardImageUploadResult[];
    startingPrice: string;
    startingPriceInCents: number;
    type: ListingType;
    minOfferPrice: string | null;
    minOfferInCents: number | null;
    autoAcceptPrice: string | null;
    autoAcceptInCents: number | null;
    scheduledTime: moment.Moment | null;
}

export interface ListingError {
    code: string;
    message: string;
}

export interface ListingResult {
    cardId: string,
    success: boolean,
    sku: { value: string },
    errors: ListingError[]
}

const useNewListing = () => {
    const create = useAuthenticatedRequestCreator<ListingResult, ListingForm>(
        (data) => {
            const {
                package: pkg,
                scheduledTime,
                type,
                cardTitle,
                frontImage, backImage, extraImages,
                grade, grader,
                startingPrice, minOfferPrice, autoAcceptPrice,
                ...rest
            } = data;

            return {
                method: "POST",
                url: "/pending-listings/ad-hoc",
                data: {
                    ...rest,
                    title: cardTitle,
                    gradeId: grade,
                    gradingCompanyId: grader,
                    frontImage: frontImage!.url,
                    backImage: backImage!.url,
                    extraImages: extraImages.map(i => i!.url),
                    type,
                    packageId: pkg!.id,
                    scheduledTime: type === ListingType.BuyItNow
                        ? null
                        : scheduledTime!.clone().tz(DefaultTimeZone, true).utc()
                }
            };
        }
    );

    const { mutateAsync: createAdHocListing, isLoading } = useMutation(create);

    return { createAdHocListing, isLoading };
};

export default useNewListing;